import React, { useRef, useState, useEffect } from 'react';

export default function PinInput({ length = 4, value = '', onChange, disabled = false }) {
  const [inputs, setInputs] = useState(new Array(length).fill(''));
  const refs = useRef([]);

  // Sync internal state with external value prop
  useEffect(() => {
    const val = String(value || '');
    const newInputs = new Array(length).fill('');
    for (let i = 0; i < Math.min(length, val.length); i++) {
        newInputs[i] = val[i];
    }
    setInputs(newInputs);
  }, [value, length]);

  const handleChange = (index, e) => {
    if (disabled) return;
    const val = e.target.value;

    // Only allow digits
    if (!/^\d*$/.test(val)) return;

    const newInputs = [...inputs];
    // Take the last char typed
    const digit = val.slice(-1);
    
    newInputs[index] = digit;
    
    // Update state and parent
    setInputs(newInputs);
    onChange(newInputs.join(''));

    // Focus next input if digit entered
    if (digit && index < length - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (disabled) return;
    
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newInputs = [...inputs];
      
      if (inputs[index]) {
        // If current box has value, clear it, stay focused
        newInputs[index] = '';
        setInputs(newInputs);
        onChange(newInputs.join(''));
      } else if (index > 0) {
        // If current box empty, clear previous and focus previous
        newInputs[index - 1] = '';
        setInputs(newInputs);
        onChange(newInputs.join(''));
        refs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      refs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^\d]/g, '').slice(0, length);
    
    if (!pastedData) return;

    const newInputs = [...inputs];
    for (let i = 0; i < pastedData.length; i++) {
        newInputs[i] = pastedData[i];
    }
    
    setInputs(newInputs);
    onChange(newInputs.join(''));
    
    // Focus the next empty box or the last box
    const nextIndex = Math.min(pastedData.length, length - 1);
    refs.current[nextIndex]?.focus();
  };

  return (
    <div className="flex justify-center gap-3">
      {inputs.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (refs.current[index] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
          disabled={disabled}
          className="w-12 h-14 text-center text-2xl font-bold border border-ptcm-border rounded-2xl bg-white/90 text-ptcm-ink focus:border-ptcm-primary focus:ring-2 focus:ring-ptcm-primary/15 outline-none transition-all disabled:opacity-50 disabled:bg-ptcm-panel dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          autoComplete="off"
        />
      ))}
    </div>
  );
}
